import { useState } from 'react';
import { useMe, useUpdateProfile } from '../../auth/authQueries';
import { Avatar } from '../../components/account/Avatar';
import { ProfileForm, type ProfileFormValues } from '../../components/account/ProfileForm';
import { CardVisitForm } from '../../components/account/CardVisitForm';
import { Alert, Card, Skeleton, pushToast } from '../../components/ui';
import { isApiHttpError } from '../../api/apiErrors';
import type { UpdateProfileRequest } from '../../api/apiSchemas';
import { env } from '../../lib/env';
import '../../components/account/account.css';

function messageForError(err: unknown): string {
  if (isApiHttpError(err) && err.code === 'HANDLE_TAKEN') {
    return 'Đường dẫn công khai này đã có người dùng. Vui lòng chọn handle khác.';
  }
  return 'Không thể lưu. Vui lòng thử lại.';
}

export default function ProfilePage() {
  const profile = useMe(true);
  const profileMutation = useUpdateProfile();
  const cardMutation = useUpdateProfile();
  const [profileError, setProfileError] = useState<string | null>(null);
  const [cardError, setCardError] = useState<string | null>(null);

  if (profile.isLoading) {
    return (
      <div className="stack">
        <h1>Hồ sơ cá nhân</h1>
        <Skeleton height="96px" />
      </div>
    );
  }

  if (profile.isError || !profile.data) {
    return (
      <div className="stack">
        <h1>Hồ sơ cá nhân</h1>
        <Alert tone="error">Không thể tải hồ sơ. Vui lòng thử lại.</Alert>
      </div>
    );
  }

  const data = profile.data;

  const profileDefaults: ProfileFormValues = {
    displayName: data.displayName ?? '',
    initials: data.initials ?? '',
    pictureUrl: data.pictureUrl ?? '',
    title: data.title ?? '',
    company: data.company ?? '',
    domain: data.domain ?? '',
    tagline: data.tagline ?? '',
    yearsExperience: data.yearsExperience != null ? String(data.yearsExperience) : '',
    midStatLabel: data.midStatLabel ?? '',
    midStatValue: data.midStatValue != null ? String(data.midStatValue) : '',
    cardHandle: data.cardHandle ?? '',
  };

  const publicUrl = data.cardHandle ? `${env.portalOrigin}/u/${data.cardHandle}` : null;

  function handleProfileSubmit(payload: UpdateProfileRequest): void {
    setProfileError(null);
    profileMutation.mutate(payload, {
      onSuccess: () => pushToast('Đã lưu hồ sơ.', 'success'),
      onError: (err) => setProfileError(messageForError(err)),
    });
  }

  function handleCardSubmit(payload: { card: UpdateProfileRequest['card']; cardPublic: boolean }): void {
    setCardError(null);
    cardMutation.mutate(payload, {
      onSuccess: () => pushToast('Đã lưu card visit.', 'success'),
      onError: (err) => setCardError(messageForError(err)),
    });
  }

  return (
    <div className="stack">
      <h1>Hồ sơ cá nhân</h1>

      <div className="summary">
        <Avatar ring size={56} displayName={data.displayName} pictureUrl={data.pictureUrl} />
        <div className="summary__info">
          <span className="summary__name">{data.displayName ?? 'Người dùng'}</span>
          <span className="summary__email">{data.email ?? 'Chưa có email'}</span>
        </div>
        {data.email ? (
          <span className={`pill ${data.emailVerified ? 'pill--verified' : 'pill--warn'}`}>
            {data.emailVerified ? 'Đã xác minh' : 'Chưa xác minh'}
          </span>
        ) : null}
      </div>

      {publicUrl ? (
        <Alert tone="info">
          Trang thẻ công khai của bạn:{' '}
          <a href={`/u/${data.cardHandle}`} target="_blank" rel="noreferrer">
            {publicUrl}
          </a>
        </Alert>
      ) : null}

      <Card>
        <h2>Thông tin hồ sơ</h2>
        <ProfileForm
          defaultValues={profileDefaults}
          onSubmit={handleProfileSubmit}
          submitting={profileMutation.isPending}
          errorMessage={profileError}
        />
      </Card>

      <Card>
        <h2>Card visit điện tử</h2>
        <CardVisitForm
          defaultCard={data.card ?? {}}
          defaultCardPublic={data.cardPublic ?? true}
          onSubmit={handleCardSubmit}
          submitting={cardMutation.isPending}
          errorMessage={cardError}
        />
      </Card>

      <p className="form-note">
        Để thay đổi email, vui lòng dùng quy trình xác minh riêng (chưa hỗ trợ tại đây).
      </p>
    </div>
  );
}
